import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, 
  User, 
  Route, 
  TrendingUp, 
  AlertTriangle,
  FileText,
  Car,
  Phone,
  Mail,
  Calendar,
  CreditCard,
  Shield,
  Smartphone
} from 'lucide-react';
import { 
  useDriverDetails,
  useDriverTrips,
  useDriverAlerts,
  useDriverDocuments,
  useDriverAssignments,
  useDriverMobileAccess
} from '@/hooks/use-driver-details';
import { format, differenceInDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export default function DriverDetails() {
  const { driverId } = useParams<{ driverId: string }>();
  const { activeCompany } = useAuth();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState('info');

  const companyId = activeCompany?.id;

  // Fetch all driver data
  const { data: driver, isLoading: driverLoading } = useDriverDetails(driverId);
  const { data: trips = [], isLoading: tripsLoading } = useDriverTrips(driverId, companyId);
  const { data: alerts = [], isLoading: alertsLoading } = useDriverAlerts(driverId, companyId);
  const { data: documents = [], isLoading: documentsLoading } = useDriverDocuments(driverId, companyId);
  const { data: assignments = [], isLoading: assignmentsLoading } = useDriverAssignments(driverId, companyId);
  const { data: mobileAccess } = useDriverMobileAccess(driverId);

  // Helper functions
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Ativo</Badge>;
      case 'inactive':
        return <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">Inativo</Badge>;
      case 'suspended':
        return <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">Suspenso</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getCNHCategoryBadge = (category?: string) => {
    if (!category) return <Badge variant="outline">N/A</Badge>;
    return <Badge variant="default">{category}</Badge>;
  };

  const getCNHValidityBadge = (validUntil?: string) => {
    if (!validUntil) return null;
    
    const daysUntilExpiry = differenceInDays(new Date(validUntil), new Date());
    
    if (daysUntilExpiry < 0) {
      return <Badge className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
        CNH Vencida
      </Badge>;
    } else if (daysUntilExpiry <= 30) {
      return <Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
        Vence em {daysUntilExpiry} dias
      </Badge>;
    }
    return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
      CNH Válida
    </Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return <Badge variant="destructive">Urgente</Badge>;
      case 'high':
        return <Badge className="bg-orange-500 text-white">Alta</Badge>;
      case 'medium':
        return <Badge className="bg-yellow-500 text-white">Média</Badge>;
      case 'low':
        return <Badge variant="outline">Baixa</Badge>;
      default:
        return <Badge variant="secondary">{priority}</Badge>;
    }
  };

  const formatCPF = (cpf?: string) => {
    if (!cpf) return 'N/A';
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  // Calculate statistics
  const statistics = {
    totalTrips: trips.length,
    completedTrips: trips.filter(t => t.status === 'completed').length,
    totalDistance: trips.reduce((sum, trip) => sum + (trip.distance_actual_km || 0), 0),
    activeAlerts: alerts.filter(a => a.status === 'active').length,
    criticalAlerts: alerts.filter(a => a.priority === 'urgent' || a.priority === 'high').length,
    averageDistance: trips.length > 0 
      ? trips.reduce((sum, trip) => sum + (trip.distance_actual_km || 0), 0) / trips.length 
      : 0,
    safetyScore: Math.max(0, 100 - (alerts.filter(a => a.priority === 'urgent' || a.priority === 'high').length * 5))
  };

  if (driverLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  if (!driver) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Motorista não encontrado</p>
            <div className="flex justify-center mt-4">
              <Button onClick={() => navigate(-1)} variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{driver.name}</h1>
              {getStatusBadge(driver.status)}
              {getCNHValidityBadge(driver.cnh_valid_until)}
            </div>
            
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
              {driver.cpf && (
                <div className="flex items-center gap-1">
                  <CreditCard className="w-4 h-4" />
                  <span>CPF: {formatCPF(driver.cpf)}</span>
                </div>
              )}
              {driver.cnh && (
                <div className="flex items-center gap-1">
                  <Shield className="w-4 h-4" />
                  <span>CNH: {driver.cnh}</span>
                  {getCNHCategoryBadge(driver.cnh_category)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total de Viagens</CardTitle>
            <Route className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.totalTrips}</div>
            <p className="text-xs text-muted-foreground">
              {statistics.completedTrips} concluídas
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Distância Total</CardTitle>
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {statistics.totalDistance.toFixed(0)} km
            </div>
            <p className="text-xs text-muted-foreground">
              Média: {statistics.averageDistance.toFixed(0)} km/viagem
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Score de Segurança</CardTitle>
            <Shield className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.safetyScore}%</div>
            <p className="text-xs text-muted-foreground">
              {statistics.criticalAlerts} alertas críticos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Alertas Ativos</CardTitle>
            <AlertTriangle className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statistics.activeAlerts}</div>
            <p className="text-xs text-muted-foreground">
              {statistics.criticalAlerts} críticos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs Content */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="info">
            <User className="w-4 h-4 mr-2" />
            Informações
          </TabsTrigger>
          <TabsTrigger value="trips">
            <Route className="w-4 h-4 mr-2" />
            Viagens
          </TabsTrigger>
          <TabsTrigger value="performance">
            <TrendingUp className="w-4 h-4 mr-2" />
            Desempenho
          </TabsTrigger>
          <TabsTrigger value="alerts">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Alertas
          </TabsTrigger>
          <TabsTrigger value="documents">
            <FileText className="w-4 h-4 mr-2" />
            Documentos
          </TabsTrigger>
          <TabsTrigger value="vehicles">
            <Car className="w-4 h-4 mr-2" />
            Veículos
          </TabsTrigger>
        </TabsList>

        {/* Tab: Informações */}
        <TabsContent value="info" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Dados Pessoais</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Nome Completo</p>
                  <p className="font-medium">{driver.name}</p>
                </div>
                {driver.cpf && (
                  <div>
                    <p className="text-sm text-muted-foreground">CPF</p>
                    <p className="font-medium">{formatCPF(driver.cpf)}</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <div className="mt-1">{getStatusBadge(driver.status)}</div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Cadastrado em</p>
                  <p className="font-medium">
                    {format(new Date(driver.created_at), "dd/MM/yyyy", { locale: ptBR })}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>CNH</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {driver.cnh ? (
                  <>
                    <div>
                      <p className="text-sm text-muted-foreground">Número da CNH</p>
                      <p className="font-medium">{driver.cnh}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Categoria</p>
                      <div className="mt-1">{getCNHCategoryBadge(driver.cnh_category)}</div>
                    </div>
                    {driver.cnh_valid_until && (
                      <div>
                        <p className="text-sm text-muted-foreground">Validade</p>
                        <p className="font-medium">
                          {format(new Date(driver.cnh_valid_until), "dd/MM/yyyy", { locale: ptBR })}
                        </p>
                        <div className="mt-1">{getCNHValidityBadge(driver.cnh_valid_until)}</div>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">CNH não cadastrada</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Contato</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {driver.phone ? (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{driver.phone}</span>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Telefone não cadastrado</p>
                )}
                {driver.email ? (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{driver.email}</span>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Email não cadastrado</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Acesso Mobile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {mobileAccess ? (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Status</span>
                      <Badge variant={mobileAccess.is_active ? "default" : "secondary"}>
                        {mobileAccess.is_active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Smartphone className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">App mobile habilitado</span>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Último acesso</p>
                      <p className="font-medium">
                        {mobileAccess.last_login_at 
                          ? format(new Date(mobileAccess.last_login_at), "dd/MM/yyyy HH:mm", { locale: ptBR })
                          : 'Nunca acessou'}
                      </p>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Acesso mobile não configurado</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab: Viagens */}
        <TabsContent value="trips">
          <Card>
            <CardHeader>
              <CardTitle>Histórico de Viagens</CardTitle>
              <CardDescription>
                {trips.length} viagens registradas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {tripsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : trips.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Veículo</TableHead>
                      <TableHead>Origem</TableHead>
                      <TableHead>Destino</TableHead>
                      <TableHead>Distância</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {trips.map((trip) => (
                      <TableRow key={trip.id}>
                        <TableCell>
                          {format(new Date(trip.start_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {trip.fleet_vehicles?.plate || 'N/A'}
                          </Badge>
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {trip.origin}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          {trip.destination}
                        </TableCell>
                        <TableCell>
                          {trip.distance_actual_km ? `${trip.distance_actual_km.toFixed(1)} km` : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={trip.status === 'completed' ? 'default' : 'secondary'}>
                            {trip.status === 'completed' ? 'Concluída' : trip.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-muted-foreground py-6">
                  Nenhuma viagem registrada
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Desempenho */}
        <TabsContent value="performance">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Média de Distância</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {statistics.averageDistance.toFixed(0)} km
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Por viagem
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Taxa de Conclusão</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">
                  {statistics.totalTrips > 0 
                    ? ((statistics.completedTrips / statistics.totalTrips) * 100).toFixed(0)
                    : 0}%
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {statistics.completedTrips}/{statistics.totalTrips} viagens
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Índice de Segurança</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">
                  {statistics.safetyScore}%
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Baseado em {alerts.length} alertas
                </p>
              </CardContent>
            </Card>
          </div>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Estatísticas Gerais</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-sm text-muted-foreground">Total de Km Percorridos</span>
                  <span className="font-bold">{statistics.totalDistance.toFixed(0)} km</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-sm text-muted-foreground">Viagens Completadas</span>
                  <span className="font-bold">{statistics.completedTrips}</span>
                </div>
                <div className="flex justify-between items-center pb-3 border-b">
                  <span className="text-sm text-muted-foreground">Alertas Críticos</span>
                  <span className="font-bold text-orange-600">{statistics.criticalAlerts}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Veículos Atribuídos</span>
                  <span className="font-bold">{assignments.filter(a => a.status === 'active').length}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Alertas */}
        <TabsContent value="alerts">
          <Card>
            <CardHeader>
              <CardTitle>Alertas e Violações</CardTitle>
              <CardDescription>
                {alerts.length} alertas registrados
              </CardDescription>
            </CardHeader>
            <CardContent>
              {alertsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : alerts.length > 0 ? (
                <div className="space-y-3">
                  {alerts.map((alert) => (
                    <Card key={alert.id}>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertTriangle className="w-4 h-4" />
                              <span className="font-medium">{alert.alert_type}</span>
                              {getPriorityBadge(alert.priority)}
                              <Badge variant={alert.status === 'active' ? 'destructive' : 'secondary'}>
                                {alert.status === 'active' ? 'Ativo' : 'Resolvido'}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {alert.description}
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">
                              {format(new Date(alert.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-6">
                  Nenhum alerta registrado
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Documentos */}
        <TabsContent value="documents">
          <Card>
            <CardHeader>
              <CardTitle>Documentação</CardTitle>
              <CardDescription>
                {documents.length} documentos cadastrados
              </CardDescription>
            </CardHeader>
            <CardContent>
              {documentsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : documents.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Validade</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell>
                          <Badge variant="outline">{doc.document_type}</Badge>
                        </TableCell>
                        <TableCell>{doc.name}</TableCell>
                        <TableCell>
                          {doc.expires_at 
                            ? format(new Date(doc.expires_at), "dd/MM/yyyy", { locale: ptBR })
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={doc.status === 'valid' ? 'default' : 'destructive'}>
                            {doc.status === 'valid' ? 'Válido' : 'Vencido'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {doc.document_url && (
                            <Button size="sm" variant="ghost" asChild>
                              <a href={doc.document_url} target="_blank" rel="noopener noreferrer">
                                Ver
                              </a>
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center text-muted-foreground py-6">
                  Nenhum documento cadastrado
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Veículos */}
        <TabsContent value="vehicles">
          <Card>
            <CardHeader>
              <CardTitle>Veículos Atribuídos</CardTitle>
              <CardDescription>
                {assignments.filter(a => a.status === 'active').length} atribuições ativas
              </CardDescription>
            </CardHeader>
            <CardContent>
              {assignmentsLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-24 w-full" />
                </div>
              ) : assignments.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {assignments.map((assignment) => (
                    <Card key={assignment.id}>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <Car className="w-8 h-8 text-muted-foreground" />
                            <div>
                              <p className="font-bold text-lg">
                                {assignment.fleet_vehicles.plate}
                              </p>
                              {assignment.fleet_vehicles.make && (
                                <p className="text-sm text-muted-foreground">
                                  {assignment.fleet_vehicles.make} {assignment.fleet_vehicles.model}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                <Calendar className="w-3 h-3 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">
                                  Desde {format(new Date(assignment.created_at), "dd/MM/yyyy", { locale: ptBR })}
                                </span>
                              </div>
                            </div>
                          </div>
                          <Badge variant={assignment.status === 'active' ? 'default' : 'secondary'}>
                            {assignment.status === 'active' ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-6">
                  Nenhum veículo atribuído
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
